import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";
import type { StepMapGeometry } from "./stepMapGeometry";

const MAX_WIDTH = 200;
const MAX_HEIGHT = 140;

function scrollToCenter(el: HTMLDivElement, x: number, y: number) {
  el.scrollLeft = x - el.clientWidth / 2;
  el.scrollTop = y - el.clientHeight / 2;
}

const Frame = styled("div")`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  padding: 10px;
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  cursor: grab;
  user-select: none;
`;

const DragShield = styled("div")`
  position: fixed;
  inset: 0;
  z-index: 9999;
  cursor: grabbing;
`;

// A canvas-space overview of the whole map with the visible region marked,
// so the expanded view keeps its sense of scale once the workflow overflows.
// The viewport rectangle tracks scrolling imperatively — no render per frame.
export function Minimap({
  geometry,
  stepColorMap,
  scrollEl,
}: {
  geometry: StepMapGeometry;
  stepColorMap: Map<string, string>;
  scrollEl: HTMLDivElement | null;
}) {
  const viewportRef = useRef<SVGRectElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const scale = Math.min(
    MAX_WIDTH / geometry.width,
    MAX_HEIGHT / geometry.height,
    1
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!scrollEl || !viewport) return;

    let raf = 0;
    const apply = () => {
      raf = 0;
      // Clamped to the canvas: a pane larger than the canvas would otherwise
      // push the rectangle past the viewBox and clip it.
      const x = Math.max(0, Math.min(scrollEl.scrollLeft, geometry.width));
      const y = Math.max(0, Math.min(scrollEl.scrollTop, geometry.height));
      const width = Math.max(
        0,
        Math.min(scrollEl.clientWidth, geometry.width - x)
      );
      const height = Math.max(
        0,
        Math.min(scrollEl.clientHeight, geometry.height - y)
      );
      viewport.setAttribute("x", String(x));
      viewport.setAttribute("y", String(y));
      viewport.setAttribute("width", String(width));
      viewport.setAttribute("height", String(height));
      // 8px corners, but never more than a quarter of the marker's smaller
      // side — on massive maps the marker is only a few pixels itself.
      viewport.setAttribute(
        "rx",
        String(Math.min(8 / scale, Math.min(width, height) / 4))
      );
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };

    apply();
    scrollEl.addEventListener("scroll", schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener("scroll", schedule);
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollEl, geometry, scale]);

  const centerOn = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!scrollEl || !svg) return;
      const rect = svg.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * geometry.width;
      const y = ((clientY - rect.top) / rect.height) * geometry.height;
      scrollToCenter(scrollEl, x, y);
    },
    [scrollEl, geometry.width, geometry.height]
  );

  const onMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    centerOn(e.clientX, e.clientY);
    setIsDragging(true);
  };

  // Drag listeners live on the window only while dragging and are torn down by
  // this effect's cleanup, so they can't leak (or call setState after unmount)
  // if the minimap disappears mid-drag — e.g. the map stops overflowing.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (move: MouseEvent) => centerOn(move.clientX, move.clientY);
    const onUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, centerOn]);

  return (
    <Frame style={isDragging ? { cursor: "grabbing" } : undefined}>
      {isDragging && <DragShield />}
      <svg
        ref={svgRef}
        width={Math.round(geometry.width * scale)}
        height={Math.round(geometry.height * scale)}
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        style={{ display: "block", overflow: "visible" }}
        onMouseDown={onMouseDown}
      >
        {[...geometry.rects.values()].map((r) => {
          // Deflate each card within its true footprint so gaps stay visible
          // once the map's real spacing compresses below a pixel.
          const insetX = Math.min(1.5 / scale, (r.right - r.left) * 0.2);
          const insetY = Math.min(1.5 / scale, (r.bottom - r.top) * 0.2);
          return (
            <rect
              key={r.id}
              x={r.left + insetX}
              y={r.top + insetY}
              width={r.right - r.left - insetX * 2}
              height={r.bottom - r.top - insetY * 2}
              rx={Math.min(4 / scale, (r.bottom - r.top - insetY * 2) / 2)}
              fill={`var(--${stepColorMap.get(r.id) ?? "gray"}-600)`}
              opacity={0.75}
            />
          );
        })}
        <rect
          ref={viewportRef}
          fill="var(--alpha4)"
          stroke="var(--text-secondary)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </Frame>
  );
}
